import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import { getContentBlocks, updateContentBlock, deleteContentBlock } from '../../../api/Settings';
import { msgStr } from '../../../common/constants/Message';
import { TextMediumSize } from '../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../common/hooks';
import { BOHTable, BOHTHead, BOHTBody, BOHTR, BOHTH, BOHTD, BOHTDIconBox } from '../../../common/components/bohtable';
import { ContentBlock } from './ContentBlockModal';

export interface ContentBlockRow extends ContentBlock {
  id: number;
  values_count: number;
  is_system: boolean;
}

interface Props {
  onEdit: (block: ContentBlockRow) => void;
  refreshTrigger: number;
  onChanged: () => void;
}

const WIDTHS = [230, 200, 100, 120, 60, 90, 90, 60, 60];

/**
 * Lists every setting_content_blocks definition for the tenant with
 * edit / activate-deactivate / delete actions. System blocks (from the API's
 * canonical catalog) are read by the storefront and booking engine, so they
 * can be edited and deactivated but never deleted.
 */
const ContentBlocksTable = ({ onEdit, refreshTrigger, onChanged }: Props) => {
  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();
  const [rows, setRows] = useState<ContentBlockRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBlocks();
  }, [refreshTrigger]);

  const fetchBlocks = () => {
    setLoading(true);
    getContentBlocks((jsonRes: any, status: number) => {
      setRows(status === 200 && Array.isArray(jsonRes) ? jsonRes : []);
      if (status !== 200) showAlert('error', jsonRes?.error || msgStr('unknownError'));
      setLoading(false);
    });
  };

  const handleResponse = (successMsg: string) => (jsonRes: any, status: number) => {
    if (status === 200) {
      showAlert('success', jsonRes?.message || successMsg);
      fetchBlocks();
      onChanged();
    } else {
      showAlert('error', jsonRes?.error || msgStr('unknownError'));
    }
  };

  const toggleActive = (block: ContentBlockRow) => {
    const next = block.is_active ? 0 : 1;
    const doToggle = () => updateContentBlock({ id: block.id, is_active: next }, handleResponse('Content block updated'));
    if (!next && block.is_system) {
      showConfirm(
        `"${block.label}" is a system block the storefront reads. Deactivating hides it from the CMS editor and the storefront API. Continue?`,
        doToggle
      );
    } else doToggle();
  };

  const removeBlock = (block: ContentBlockRow) => {
    const impact = block.values_count > 0 ? ` This will also delete its saved values for ${block.values_count} brand(s).` : '';
    showConfirm(`Delete content block "${block.label}" (${block.key_name})?${impact}`, () => {
      deleteContentBlock(block.id, handleResponse('Content block deleted'));
    });
  };

  return (
    <BOHTable isLoading={loading}>
      <BOHTHead>
        <BOHTR>
          <BOHTH width={WIDTHS[0]}>Key</BOHTH>
          <BOHTH width={WIDTHS[1]}>Label</BOHTH>
          <BOHTH width={WIDTHS[2]}>Type</BOHTH>
          <BOHTH width={WIDTHS[3]}>Group</BOHTH>
          <BOHTH width={WIDTHS[4]} style={{ textAlign: 'center' }}>Sort</BOHTH>
          <BOHTH width={WIDTHS[5]} style={{ textAlign: 'center' }}>Brands Set</BOHTH>
          <BOHTH width={WIDTHS[6]} style={{ textAlign: 'center' }}>Active</BOHTH>
          <BOHTH width={WIDTHS[7]} style={{ textAlign: 'center' }}>Edit</BOHTH>
          <BOHTH width={WIDTHS[8]} style={{ textAlign: 'center' }}>Del</BOHTH>
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {rows.length > 0 ? (
          rows.map((item) => (
            <BOHTR key={item.id}>
              <BOHTD width={WIDTHS[0]}>
                <View style={styles.keyCell}>
                  <Text style={styles.keyText}>{item.key_name}</Text>
                  {item.is_system && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>system</Text>
                    </View>
                  )}
                </View>
              </BOHTD>
              <BOHTD width={WIDTHS[1]}>{item.label}</BOHTD>
              <BOHTD width={WIDTHS[2]}>{item.field_type}</BOHTD>
              <BOHTD width={WIDTHS[3]}>{item.group_name || '-'}</BOHTD>
              <BOHTD width={WIDTHS[4]} textAlign="center">{item.sort_order ?? 0}</BOHTD>
              <BOHTD width={WIDTHS[5]} textAlign="center">{item.values_count}</BOHTD>
              <BOHTD width={WIDTHS[6]} textAlign="center">
                <TouchableOpacity onPress={() => toggleActive(item)}>
                  <FontAwesome5
                    name={item.is_active ? 'check-circle' : 'times-circle'}
                    size={TextMediumSize}
                    color={item.is_active ? '#2E7D32' : '#D32F2F'}
                  />
                </TouchableOpacity>
              </BOHTD>
              <BOHTDIconBox width={WIDTHS[7]}>
                <TouchableOpacity onPress={() => onEdit(item)}>
                  <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
                </TouchableOpacity>
              </BOHTDIconBox>
              <BOHTDIconBox width={WIDTHS[8]}>
                {item.is_system ? (
                  <FontAwesome5 size={TextMediumSize} name="lock" color="#bbb" />
                ) : (
                  <TouchableOpacity onPress={() => removeBlock(item)}>
                    <FontAwesome5 size={TextMediumSize} name="times" color="black" />
                  </TouchableOpacity>
                )}
              </BOHTDIconBox>
            </BOHTR>
          ))
        ) : (
          !loading && (
            <View style={styles.empty}>
              <Text style={{ color: '#999' }}>No content blocks found</Text>
            </View>
          )
        )}
      </BOHTBody>
    </BOHTable>
  );
};

const styles = StyleSheet.create({
  keyCell: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  keyText: { fontFamily: 'monospace', fontSize: 12 },
  badge: { backgroundColor: '#e5e7eb', paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8 },
  badgeText: { fontSize: 10, color: '#374151', fontWeight: '600' },
  empty: { padding: 20, alignItems: 'center' },
});

export default ContentBlocksTable;
